/* eslint-disable import/order */
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
// Serialization
import { deserialize, serialize } from 'serializr';
import {
  onDemandAccessSchema,
  onDemandAccessForCheckoutSchema,
  onDemandAccessSelfCheckoutSchema,
  onDemandAccessExtendCheckoutSchema,
} from './schemas';
// Utils
import permissions from '../../constants/Permissions';
// Color
import { parkingStatusPalette } from '../../components/core/colors';
// Models
import mParking from './parking';
// Fragments
import { onDemandAccessFragment, onDemandAccessForCheckoutFragment, operationFragment } from './fragments';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { findInList } from '../../utils/Tools';

// Permissions used in a model
const {
  pCpm: { parkingWrite, customerRead, parkingCategoryRead },
} = permissions;

/**
 * Serialization
 */
const deserializeOnDemandAccess = (data) => deserialize(onDemandAccessSchema, data);
const deserializeOnDemandAccessForCheckout = (data) => deserialize(onDemandAccessForCheckoutSchema, data);

const serializeOnDemandSelfCheckout = ({
  estimatedEnd,
  locale,
  reference,
  type,
}) => ({
  ...serialize(onDemandAccessSelfCheckoutSchema, {
    ...(!_isNil(estimatedEnd) && { estimatedEnd }),
    ...(!_isNil(locale) && { locale }),
    reference,
    type,
  }),
});

const serializeOnDemandExtendCheckout = ({
  emailPrefix,
  estimatedEnd,
  reference,
}) => ({
  ...serialize(onDemandAccessExtendCheckoutSchema, {
    emailPrefix,
    estimatedEnd,
    reference,
  }),
});

/**
 * Lists
 */

const onDemandStatusList = [
  {
    key: mParking.STATUS_LIST.CREATED,
    value: mParking.STATUS_LIST.CREATED,
    labelId: 'onDemand.status.created',
    icon: 'parkingStatusCreated',
    color: parkingStatusPalette.created,
  },
  {
    key: mParking.STATUS_LIST.IN_PROGRESS,
    value: mParking.STATUS_LIST.IN_PROGRESS,
    labelId: 'onDemand.status.inProgress',
    icon: 'parkingStatusInProgress',
    color: parkingStatusPalette.inProgress,
  },
  {
    key: mParking.STATUS_LIST.STARTED,
    value: mParking.STATUS_LIST.STARTED,
    labelId: 'onDemand.status.started',
    icon: 'parkingStatusStarted',
    color: parkingStatusPalette.started,
  },
  {
    key: mParking.STATUS_LIST.IN_OVERSTAY,
    value: mParking.STATUS_LIST.IN_OVERSTAY,
    labelId: 'onDemand.status.inOverstay',
    icon: 'parkingStatusInOverstay',
    color: parkingStatusPalette.inOverstay,
  },
  {
    key: mParking.STATUS_LIST.FINISHED,
    value: mParking.STATUS_LIST.FINISHED,
    labelId: 'onDemand.status.finished',
    icon: 'parkingStatusFinished',
    color: parkingStatusPalette.finished,
  },
  {
    key: mParking.STATUS_LIST.CANCELLED,
    value: mParking.STATUS_LIST.CANCELLED,
    labelId: 'onDemand.status.cancelled',
    icon: 'parkingStatusCancelled',
    color: parkingStatusPalette.cancelled,
  },
];

const onDemandSelfCheckoutTypeList = [
  {
    key: 'definitive_exit',
    value: 'definitive_exit',
    labelId: 'onDemand.selfCheckoutType.definitiveExit',
    icon: 'onDemandSelfCheckoutDefinitiveExit',
  },
  {
    key: 'temporary_exit',
    value: 'temporary_exit',
    labelId: 'onDemand.selfCheckoutType.temporaryExit',
    icon: 'onDemandSelfCheckoutTemporaryExit',
  },
  {
    key: 'not_done',
    value: 'not_done',
    labelId: 'onDemand.selfCheckoutType.notDone',
    icon: 'onDemandSelfCheckoutNotDone',
  },
  {
    key: 'drop_off',
    value: 'drop_off',
    labelId: 'onDemand.selfCheckoutType.dropOffExit',
    icon: 'onDemandSelfCheckoutDropOffExit',
  },
];

// Status
const onDemandStatus = (parking) => findInList(onDemandStatusList, 'value', parking, 'status');

/**
 * Getters
 */
// SelfCheckoutType
const getSelfCheckoutTypeDefinitiveExit = onDemandSelfCheckoutTypeList[0];
const getSelfCheckoutTypeTemporaryExit = onDemandSelfCheckoutTypeList[1];
// const getSelfCheckoutTypeNotDone = onDemandSelfCheckoutTypeList[2];
const getSelfCheckoutTypeDropOff = onDemandSelfCheckoutTypeList[3];

/**
 * Utils
 */

// SelfCheckoutType
const onDemandGetSelfCheckoutType = (onDemand) => {
  let selfCheckoutType = null;
  if (_get(onDemand, 'selfCheckoutType')) {
    selfCheckoutType = onDemandSelfCheckoutTypeList.find((type) => type.value === onDemand.selfCheckoutType.toLowerCase() || type.value === onDemand.selfCheckoutType.toUpperCase());
  }

  return selfCheckoutType;
};

const hasPriceGrid = (onDemand) => !_isNil(onDemand.priceGrid);
const isDefinitiveExit = (onDemand) => _get(onDemandGetSelfCheckoutType(onDemand), 'value') === getSelfCheckoutTypeDefinitiveExit.value;
const isTemporaryExit = (onDemand) => _get(onDemandGetSelfCheckoutType(onDemand), 'value') === getSelfCheckoutTypeTemporaryExit.value;
const isDropOff = (onDemand) => _get(onDemandGetSelfCheckoutType(onDemand), 'value') === getSelfCheckoutTypeDropOff.value;
const isSelfCheckoutAlreadyDone = (onDemand) => !_isNil(_get(onDemand, 'selfCheckoutType'));
const getParkingCategory = (onDemand, customer) => _get(onDemand, 'parkingCategory') || _get((customer || onDemand.customer), 'parkingCategory');
const getParkingCategoryId = (onDemand, customer) => _get(getParkingCategory(onDemand, customer), 'id');
const isFinished = (onDemand) => onDemand.status === mParking.STATUS_LIST.FINISHED;
const isEligibleToCancel = (onDemand) => _get(onDemand, 'isDeletable', false);
const isEligibleToDeclareDefinitiveExit = (onDemand) => onDemand.active
  && isTemporaryExit(onDemand)
  && mParking.isInOverstay(onDemand);

const isEligibleToExtension = (onDemand) => onDemand.active
    && isSelfCheckoutAlreadyDone(onDemand)
    && (mParking.isInProgress(onDemand) || mParking.isStarted(onDemand));

const isEligibleToOverstay = (onDemand) => onDemand.active
    && isSelfCheckoutAlreadyDone(onDemand)
    && !isDefinitiveExit(onDemand)
    && mParking.isInOverstay(onDemand);

/**
 * GraphQL
 */

const queSearchOnDemandAccess = `
  query SearchOnDemandAccess($input: SearchOnDemandAccessInput!) {
    searchOnDemandAccess(input: $input) {
      ...onDemandAccessForCheckoutFragment
    }
  }
  ${onDemandAccessForCheckoutFragment}
`;

const queGetOnDemandAccess = `
  query GetOnDemandAccess($input: GetOnDemandAccessInput!) {
    getOnDemandAccess(input: $input) {
      ...onDemandAccessForCheckoutFragment
    }
  }
  ${onDemandAccessForCheckoutFragment}
`;

const mutSelfCheckoutOnDemandAccess = `
  mutation SelfCheckoutOnDemandAccess($input: SelfCheckoutOnDemandAccessInput!) {
    selfCheckoutOnDemandAccess(input: $input) {
      ...onDemandAccessForCheckoutFragment
    }
  }
  ${onDemandAccessForCheckoutFragment}
`;

const mutExtendOnDemandAccess = `
  mutation ExtendOnDemandAccess($input: ExtendOnDemandAccessInput!) {
    extendOnDemandAccess(input: $input) {
      ...onDemandAccessForCheckoutFragment
    }
  }
  ${onDemandAccessForCheckoutFragment}
`;

const mutPayOnDemandAccessOverstay = `
  mutation PayOnDemandAccessOverstay($input: PayOnDemandAccessOverstayInput!) {
    payOnDemandAccessOverstay(input: $input) {
      ...onDemandAccessForCheckoutFragment
    }
  }
  ${onDemandAccessForCheckoutFragment}
`;

const mutDeleteOnDemand = `
  mutation DeleteOnDemandAccess($input: DeleteOnDemandAccessInput!) {
    deleteOnDemandAccess(input: $input) {
      id
    }
  }
`;

const queListOnDemandAccesses = `
  query ListOnDemandAccesses($input: ListOnDemandAccessesInput!, $pagination: PaginationInput, $search: ListOnDemandAccessesSearchInput, $sort: [ListOnDemandAccessesSortInput]) {
    listOnDemandAccesses(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...onDemandAccessFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }
  ${onDemandAccessFragment}
`;

const mutCategorizeOnDemandAccess = `
  mutation CategorizeOnDemandAccess($input: CategorizeOnDemandAccessInput!) {
    categorizeOnDemandAccess(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutCreateOnDemandAccess = `
  mutation CreateOnDemandAccess($input: CreateOnDemandAccessInput!) {
    createOnDemandAccess(input: $input) {
      ...onDemandAccessFragment
    }
  }
  ${onDemandAccessFragment}
`;

const mutUpdateOnDemandAccessState = `
  mutation UpdateOnDemandAccessState($input: UpdateOnDemandAccessStateInput!) {
    updateOnDemandAccessState(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutDeclareFinalExitForTemporaryOnDemandAccess = `
  mutation DeclareFinalExitForTemporaryOnDemand($input: DeclareFinalExitForTemporaryOnDemandAccessInput!) {
    declareFinalExitForTemporaryOnDemandAccess(input: $input) {
      id
    }
}
`;

const mutTerminateOnDemandAccess = `
  mutation TerminateOnDemandAccess($input: TerminateOnDemandAccessInput!) {
    terminateOnDemandAccess(input: $input) {
      id
    }
  }
`;

const mutUpdateOnDemandAccessCustomer = `
  mutation UpdateOnDemandAccessCustomer($input: UpdateOnDemandAccessCustomerInput!) {
    updateOnDemandAccessCustomer(input: $input){
      id
    }
  }
`;

const mutUpdateOnDemandAccess = `
  mutation UpdateOnDemandAccess($input: UpdateOnDemandAccessInput!) {
    updateOnDemandAccess(input: $input) {
      id
    }
  }
`;

const mutRefundOnDemandAccess = `
  mutation RefundOnDemandAccess($input: RefundOnDemandAccessInput!) {
    refundOnDemandAccess(input: $input) {
      status
    }
  }
`;

/**
 * Model
 */
const onDemand = {
  // Name and icon
  name: 'onDemand',
  icon: 'onDemand',

  onDemandSelfCheckoutTypeList,

  // Serialization
  deserialize: deserializeOnDemandAccess,
  deserializeForCheckout: deserializeOnDemandAccessForCheckout,
  serializeSelfCheckout: serializeOnDemandSelfCheckout,
  serializeExtendCheckout: serializeOnDemandExtendCheckout,

  // Utils
  getSelfCheckoutTypeDefinitiveExit,
  getSelfCheckoutTypeTemporaryExit,
  getSelfCheckoutType: onDemandGetSelfCheckoutType,
  hasPriceGrid,
  isDefinitiveExit,
  isSelfCheckoutAlreadyDone,
  getParkingCategory,
  getParkingCategoryId,
  isEligibleToExtension,
  isEligibleToDeclareDefinitiveExit,
  isEligibleToCancel,
  isEligibleToOverstay,
  isFinished,
  isTemporaryExit,
  isDropOff,

  // Lists
  status: onDemandStatus,
  statusList: onDemandStatusList,

  // List features
  listFeatures: ({ withQuickParkingCategory } = {}) => ({
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'status', dir: 'asc' },
        { field: 'begin', dir: 'desc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {},

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 3 + ((withQuickParkingCategory
          && hasPermission({ needed: parkingWrite, permissionsOrContext: 'carparkPermissions' })
          && hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }))
          | 0),
      sm: 3 + ((withQuickParkingCategory
          && hasPermission({ needed: parkingWrite, permissionsOrContext: 'carparkPermissions' })
          && hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }))
          | 0),
      md: 3 + ((withQuickParkingCategory
        && hasPermission({ needed: parkingWrite, permissionsOrContext: 'carparkPermissions' })
        && hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }))
        | 0),
      lg:
        8
        + ((withQuickParkingCategory
          && hasPermission({ needed: parkingWrite, permissionsOrContext: 'carparkPermissions' })
          && hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }))
          | 0),
      xl:
        9
        + ((withQuickParkingCategory
          && hasPermission({ needed: parkingWrite, permissionsOrContext: 'carparkPermissions' })
          && hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }))
          | 0),
    }),
    columns: [
      {
        key: 'status',
        labelId: ' ',
        field: 'status',
        sortable: true,
        secondarySorts: [{ field: 'begin', dir: 'desc' }],
        className: 'table-content-icon table-content-center',
      },
      {
        key: 'reference',
        labelId: 'field.reference',
        field: 'reference',
        sortable: true,
        className: 'table-content-1 hidden-md-down',
      },
      {
        key: 'begin',
        labelId: 'field.entryDate',
        field: 'begin',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'end',
        labelId: 'field.endDate',
        field: 'end',
        sortable: true,
        className: 'hidden-md-down',
      },
      {
        key: 'licensePlate',
        labelId: 'field.licensePlate',
        field: 'licensePlate',
        sortable: false,
      },
      {
        key: 'parkingCategoryId',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'parkingCategoryId',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'typeOfExit',
        labelId: 'field.typeOfExit',
        field: 'typeOfExit',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'payment',
        labelId: 'field.payment',
        field: 'payment',
        sortable: false,
        className: 'table-content-1-5 hidden-md-down',
      },
      {
        key: 'occupancy',
        labelId: 'field.occupancy',
        field: 'occupancy',
        sortable: false,
        className: 'table-content-1 hidden-lg-down',
      },
      {
        key: 'quickCategorization',
        hidden: !withQuickParkingCategory,
        permissions: {
          needed: `${parkingWrite}&&${customerRead}`,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.quickCategorization',
        field: 'quickCategorization',
        sortable: false,
        className: 'table-content-center',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
      {
        key: 'reference',
        labelId: 'field.reference',
        field: 'reference',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'reference',
        },
      },
      {
        key: 'licensePlate',
        labelId: 'field.licensePlate',
        field: 'licensePlate',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'licensePlate',
        },
      },
      {
        key: 'selfCheckoutType',
        labelId: 'field.selfCheckoutType',
        field: 'selfCheckoutType',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        withOperators: true,
        filter: {
          field: 'selfCheckoutType',
        },
      },
      {
        key: 'isInside',
        labelId: 'field.isInside',
        field: 'isInside',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'isInside',
        },
      },
      {
        key: 'beginBefore',
        labelId: 'field.beforeBeginDate',
        field: 'beginBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'beginAfter',
        labelId: 'field.afterBeginDate',
        field: 'beginAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'begin',
        },
      },
      {
        key: 'endBefore',
        labelId: 'field.beforeEndDate',
        field: 'endBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'endAfter',
        labelId: 'field.afterEndDate',
        field: 'endAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'end',
        },
      },
      {
        key: 'creationDateBefore',
        labelId: 'field.beforeCreationDate',
        field: 'creationDateBefore',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'creationDate',
        },
      },
      {
        key: 'creationDateAfter',
        labelId: 'field.afterCreationDate',
        field: 'creationDateAfter',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'creationDate',
        },
      },
      {
        key: 'customerParkingCategoryId',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'customerParkingCategoryId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'parking_category_id',
        },
      },
    ],
  }),

  // GraphQL
  queGetOnDemandAccess,
  queSearchOnDemandAccess,
  mutSelfCheckoutOnDemandAccess,
  mutExtendOnDemandAccess,
  mutPayOnDemandAccessOverstay,
  mutDeleteOnDemand,
  queListOnDemandAccesses,
  mutCategorizeOnDemandAccess,
  mutCreateOnDemandAccess,
  mutUpdateOnDemandAccessState,
  mutRefundOnDemandAccess,
  mutDeclareFinalExitForTemporaryOnDemandAccess,
  mutTerminateOnDemandAccess,
  mutUpdateOnDemandAccessCustomer,
  mutUpdateOnDemandAccess,
};

module.exports = onDemand;



// WEBPACK FOOTER //
// ./models/cpm/onDemand.js